import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from 'react-intl';
import {
    Box,
    Dialog,
    DialogContent,
    DialogContentText,
    IconButton,
    Slide,
    useMediaQuery, useTheme,
    Button,
    Typography,
    Divider, Tooltip
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

import { List as IList } from 'immutable';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getBrandElectronicCurrencyList, getBrandPlasticCurrencyList } from '../brand/brandSelectors';
import { getIntlCurrencyCode } from '../intl/intlSelectors';
import { setCurrency as setCurrencyActionCreator } from '../intl/intlModule';
import giftCardDetailsPageMessagesV2 from './messages/giftCardDetailsPageMessages';
import { getCurrencySymbol } from '../intl/getCurrencySymbol';

import CurrencyPicker from '../localeCurrencyPicker/CurrencyPicker';
import { CARD_TYPES } from '../constants/constants';
import { getCartCurrencyCode } from '../productCartHandler/productCartSelectors';
import homePageV2Messages from '../landingPage_v2/assets/messages/homePageMessages';
import CartCurrencyPresentPopUp from '../localeCurrencyPicker/CartCurrencyPresentPopUp';
import { getFormattedMessage } from '../utils/textUtils';

const Transition = React.forwardRef((props, ref) => <Slide direction='up' ref={ref} {...props} />);

const DenomCurrencyPicker = ({
    intl, selectedCurrency, setCurrency, supportedElectronicCurrencyList, supportedPlasticCurrencyList, cardDeliveryType, cartCurrencyCode, tempCurrency, setTempCurrency, isSetCurrency
}) => {
    const shouldShowGiftCardsRedeemableInfo = getFormattedMessage(intl, 'currencyLocalePicker.currencyLocaleMessages.giftCardsRedeemableInfoText');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [openDialog, setOpenDialog] = useState(false);

    const [open, setOpen] = React.useState(false);
    let newTempCurrency;
    if (tempCurrency !== '') {
        newTempCurrency = tempCurrency;
    } else {
        newTempCurrency = selectedCurrency;
    }

    const currencies = (cardDeliveryType === CARD_TYPES.Physical
        ? supportedPlasticCurrencyList
        : supportedElectronicCurrencyList
    ).toArray();

    const currencyList = currencies
        .map((cur) => {
            const symbol = getCurrencySymbol(intl, cur);
            return `${cur} ${symbol.prefix}${symbol.suffix}`;
        })
        .join(', ');

    const hideDenomPicker = supportedPlasticCurrencyList.size <= 1 && supportedElectronicCurrencyList.size <= 1;

    const showInfoIcon = (cardDeliveryType === CARD_TYPES.Physical
        ? supportedPlasticCurrencyList.size > 1
        : supportedElectronicCurrencyList.size > 1);

    const isSingleCurrency = (cardDeliveryType === CARD_TYPES.Physical ? supportedPlasticCurrencyList.size === 1
        : supportedElectronicCurrencyList.size === 1);

    const handleOpen = () => {
        if (cartCurrencyCode || isSingleCurrency) {
            return;
        }
        setOpen(true);
    };
    const handleOnCancel = () => {
        setOpen(false);
    };

    const currencySymbol = getCurrencySymbol(intl, newTempCurrency).prefix
        + getCurrencySymbol(intl, newTempCurrency).suffix;

    const handleIconClick = () => {
        setOpenDialog(true);
    };

    const handleCloseModel = () => {
        setOpenDialog(false);
    };

    let iconContent;
    if (cartCurrencyCode && showInfoIcon) {
        if (isMobile) {
            iconContent = (
                <>
                    <InfoOutlinedIcon className='custom-tooltip' onClick={handleIconClick} style={{ cursor: 'pointer' }} />
                    {openDialog && (
                        <CartCurrencyPresentPopUp
                            isOpen={openDialog}
                            handleCloseModel={handleCloseModel}
                            message={intl.formatMessage(homePageV2Messages.cartCurrencyExists, {
                                selectedCurrency,
                                cardDeliveryType,
                                currencyList
                            })}
                        />
                    )}
                </>
            );
        } else {
            iconContent = (
                <Tooltip
                    title={(
                        <span style={{ whiteSpace: 'pre-line' }}>
                            {intl.formatMessage(homePageV2Messages.cartCurrencyExists, {
                                selectedCurrency,
                                cardDeliveryType,
                                currencyList
                            })}
                        </span>
                    )}
                    placement="top"
                    arrow
                    className='custom-tooltip'
                >
                    <InfoOutlinedIcon />
                </Tooltip>
            );
        }
    } else if (!isSingleCurrency) {
        iconContent = <ExpandMoreIcon />;
    }

    const renderCurrencyDisplay = () => {
        if (hideDenomPicker) {
            return null;
        }

        if (cartCurrencyCode) {
            return (
                <div className='DenomCurrencyPicker DenomCurrencyPickerStatic'>
                    <div className='DenomCurrencyPickerText'>
                        <Typography component='span' className='pickerTextWrap'>
                            {`${newTempCurrency} (${currencySymbol})`}
                        </Typography>
                        <span className="visually-hidden-span"> - Change currency</span>
                        {iconContent}
                    </div>
                </div>
            );
        }

        if (isSingleCurrency) {
            return (
                <div className='DenomCurrencyPicker DenomCurrencyPickerStatic'>
                    <div className='DenomCurrencyPickerText'>
                        <Typography component='span' className='pickerTextWrap'>
                            {`${newTempCurrency} (${currencySymbol})`}
                        </Typography>
                    </div>
                </div>
            );
        }

        return (
            <Button className='DenomCurrencyPicker' onClick={handleOpen}>
                <div className='DenomCurrencyPickerText'>
                    <Typography component='span' className='pickerTextWrap'>
                        {`${newTempCurrency} (${currencySymbol})`}
                    </Typography>
                    <span className="visually-hidden-span"> - Change currency</span>
                    {iconContent}
                </div>
            </Button>
        );
    };

    return (
        <>
            {renderCurrencyDisplay()}
            <Dialog
                open={open}
                className='locale-currency-popup-modal'
                onClose={() => { handleOnCancel(); }}
                scroll='paper'
                TransitionComponent={isMobile ? Transition : undefined}
                fullWidth
                maxWidth='sm'
                aria-labelledby='alert-dialog-title'
                aria-describedby='alert-dialog-description'
            >
                <Box>
                    <Typography id='alert-dialog-title' component='h2' className='pdpCurrencyPickerTitle'>
                        {intl.formatMessage(giftCardDetailsPageMessagesV2.CurrencyPickerModalHeader)}
                    </Typography>
                    <IconButton
                        className='close-currency-locale-modal'
                        aria-label="Close"
                        onClick={() => { handleOnCancel(); }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent className='tab-content'>
                    <DialogContentText>
                        <Box className='currency-locale-wrapper'>
                            {shouldShowGiftCardsRedeemableInfo.trim() && (
                                <Typography component='p' className='giftCardsRedeemableInfoText'>
                                    {shouldShowGiftCardsRedeemableInfo}
                                </Typography>
                            )}
                            <Divider />
                            <CurrencyPicker
                                intl={intl}
                                setCurrency={setCurrency}
                                selectedCurrency={selectedCurrency}
                                popupStatus={handleOnCancel}
                                setTempCurrency={setTempCurrency}
                                tempCurrency={tempCurrency}
                                location='PDP'
                                isSetCurrency={isSetCurrency}
                                supportedCurrencyList={cardDeliveryType === CARD_TYPES.Physical
                                    ? supportedPlasticCurrencyList : supportedElectronicCurrencyList}
                            />
                        </Box>
                    </DialogContentText>
                </DialogContent>
            </Dialog>
        </>
    );
};

DenomCurrencyPicker.defaultProps = {
    supportedElectronicCurrencyList: IList(),
    supportedPlasticCurrencyList: IList(),
    cardDeliveryType: '',
    isSetCurrency: true,
    setTempCurrency: () => {},
    tempCurrency: ''
};

DenomCurrencyPicker.propTypes = {
    
    intl: intlShape.isRequired,
    supportedElectronicCurrencyList: PropTypes.instanceOf(IList),
    setCurrency: PropTypes.func.isRequired,
    selectedCurrency: PropTypes.string.isRequired,
    supportedPlasticCurrencyList: PropTypes.instanceOf(IList),
    cardDeliveryType: PropTypes.string,
    isSetCurrency: PropTypes.bool,
    tempCurrency: PropTypes.string,
    cartCurrencyCode: PropTypes.string.isRequired,
    setTempCurrency: PropTypes.func
};

export const mapStateToProps = state => ({
    selectedCurrency: getIntlCurrencyCode(state),
    supportedElectronicCurrencyList: getBrandElectronicCurrencyList(state),
    supportedPlasticCurrencyList: getBrandPlasticCurrencyList(state),
    cartCurrencyCode: getCartCurrencyCode(state)
});

export const mapDispatchToProps = {
    setCurrency: setCurrencyActionCreator
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(DenomCurrencyPicker));
